import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { FallbackProps } from 'react-error-boundary'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { markAutoLogout, shouldAutoLogout } from '@/utils/autoLogout'
import { showToast } from '@/utils/toastMessage'

const Error = ({ error, resetErrorBoundary }: FallbackProps) => {
  const { logout } = useAuth()

  // The API rejected the token itself (e.g. a session from another Auth0 tenant/environment).
  // Auth0 still reports the user as authenticated, so retrying with the same token can never
  // succeed — the only way out is ending the session and logging in again.
  const isUnauthorized = isAxiosError(error) && error.response?.status === 401
  const [autoLoggingOut] = useState(() => isUnauthorized && shouldAutoLogout())

  const relogin = () => logout({ logoutParams: { returnTo: window.location.origin } })

  useEffect(() => {
    if (autoLoggingOut) {
      markAutoLogout()
      relogin()
    }
  }, [autoLoggingOut])

  if (autoLoggingOut) return null

  // Check if this is a timeout error and handle it gracefully
  const errorMessage = (error as Error)?.message || ''
  const errorCode = (error as any)?.code
  const isTimeoutError =
    errorMessage.includes('timeout') ||
    errorCode === 'ECONNABORTED' ||
    errorMessage.includes('Network Error')

  if (isTimeoutError) {
    // Show toast notification instead of error page for timeouts
    showToast({
      severity: 'error',
      summary: 'Zeitüberschreitung',
      detail:
        'Die Anfrage hat zu lange gedauert. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es später erneut.',
    })
    // Reset the error boundary to recover
    resetErrorBoundary()
    return null // Don't render the error page
  }

  return (
    <div className="m-5 flex h-screen w-screen flex-col items-center justify-center gap-5 text-red-800">
      <img alt="xeetr logo" src="/xeetr-logo-dark.png" className="h-20" />
      {isUnauthorized ? (
        <p>Ihre Anmeldung ist ungültig. Bitte melden Sie sich erneut an.</p>
      ) : (
        <p>Hoppla, da ist wohl etwas schief gelaufen...</p>
      )}
      <div className="flex gap-3">
        {!isUnauthorized && (
          <Button className="h-fit" onClick={() => resetErrorBoundary()}>
            Neuladen
          </Button>
        )}
        <Button
          className="h-fit"
          variant={isUnauthorized ? 'default' : 'outline'}
          onClick={relogin}
        >
          Abmelden &amp; neu anmelden
        </Button>
      </div>
      {import.meta.env.MODE !== 'prod' && (
        <div className="text-black">{(error as Error).message}</div>
      )}
    </div>
  )
}

export default Error
